import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaExchangeAlt, FaUsers, FaChartBar, FaCog, FaSignOutAlt, FaUserCog, FaReceipt, FaBalanceScale } from 'react-icons/fa';
import AdminAPI from '../../utils/adminApi';
import terms from '../../config/terminology';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const adminName = localStorage.getItem('adminName') || 'Admin';
  const [stats, setStats] = useState({
    totalDonators: 0,
    totalCreators: 0,
    totalDonations: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch creators count
      const creatorsData = await AdminAPI.get('/api/manage/creators/count');
      setStats(prev => ({ ...prev, totalCreators: creatorsData.count }));
      
      // Fetch donators count
      const donatorsData = await AdminAPI.get('/api/manage/donators/count');
      setStats(prev => ({ ...prev, totalDonators: donatorsData.count }));

      // Fetch donations count
      const donationsData = await AdminAPI.get('/api/manage/donations/count');
      setStats(prev => ({ ...prev, totalDonations: donationsData.completed_count }));

    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = () => {
    AdminAPI.redirectToLogin();
  };

  const adminLinks = [
    {
      title: 'User Management',
      description: 'Manage user accounts, reset passwords, and view user details',
      icon: FaUserCog,
      path: '/manage/users',
      color: 'bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400'
    },
    {
      title: 'Transfer Management',
      description: 'Create manual transfers to connected accounts',
      icon: FaExchangeAlt,
      path: '/manage/transfer',
      color: 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
    },
    {
      title: `${terms.Donator} Management`,
      description: `View and manage ${terms.donator} accounts`,
      icon: FaUsers,
      path: '/manage/donators',
      color: 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
    },
    {
      title: `${terms.Donations} Ledger`,
      description: `View all ${terms.donations} and tips on the platform`,
      icon: FaReceipt,
      path: '/manage/donations',
      color: 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
    },
    {
      title: 'Reconciliation',
      description: 'Creator balances and payouts (Stripe source of truth)',
      icon: FaBalanceScale,
      path: '/manage/reconciliation',
      color: 'bg-teal-500/20 hover:bg-teal-500/30 text-teal-400'
    },
    {
      title: 'Analytics',
      description: 'View platform statistics (Coming soon)',
      icon: FaChartBar,
      path: '#',
      color: 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400',
      disabled: true
    },
    {
      title: 'Settings',
      description: 'Configure platform settings and user registration',
      icon: FaCog,
      path: '/manage/settings',
      color: 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-400'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">Welcome back, {adminName}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition"
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {adminLinks.map((link, index) => {
            const Icon = link.icon;
            const Component = link.disabled ? 'div' : Link;
            
            return (
              <Component
                key={index}
                to={link.path}
                className={`block p-6 rounded-lg border border-gray-800 transition ${
                  link.disabled 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:border-gray-700 cursor-pointer'
                } ${link.color}`}
              >
                <div className="flex items-start space-x-4">
                  <div className="p-3 rounded-lg bg-black/50">
                    <Icon className="text-2xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-1">{link.title}</h3>
                    <p className="text-sm opacity-80">{link.description}</p>
                    {link.disabled && (
                      <span className="inline-block mt-2 text-xs bg-gray-800 px-2 py-1 rounded">
                        Coming Soon
                      </span>
                    )}
                  </div>
                </div>
              </Component>
            );
          })}
        </div>

        <div className="mt-12 p-6 bg-gray-900 rounded-lg border border-gray-800">
          <h2 className="text-xl font-semibold mb-4">Quick Stats</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-800 p-4 rounded">
              <p className="text-gray-400 text-sm">Total {terms.Donators}</p>
              <p className="text-2xl font-bold">{loading ? '...' : stats.totalDonators}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded">
              <p className="text-gray-400 text-sm">Total Creators</p>
              <p className="text-2xl font-bold">{loading ? '...' : stats.totalCreators}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded">
              <p className="text-gray-400 text-sm">Total {terms.Donations}</p>
              <p className="text-2xl font-bold">{loading ? '...' : stats.totalDonations}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;